import { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// ✅ Solid icons
import {
  faEnvelope,
  faGlobe,
  faPhone,
  faLocationDot
} from "@fortawesome/free-solid-svg-icons";

// ✅ Brand icons
import {
  faInstagram,
  faFacebookF,
  faLinkedinIn,
  faPinterestP,
  faYoutube,
  faThreads,
  faXTwitter,
  faWhatsapp
} from "@fortawesome/free-brands-svg-icons";
import assetUrl from "../utils/assetUrl";
// ─── Asset Imports ─────────────────────────────────────────────────────────────
// Update these paths to match your project structure
import imgGetInTouch   from "/assets/images/contact-us/rethinkways-contact-us-01-BDX1JNnS.png";
import imgWorkWithUs   from "/assets/images/contact-us/rethinkways-contact-us-02-BTjd_Mci.png";
import imgPartnerWith  from "/assets/images/contact-us/rethinkways-contact-us-03-CpLMUXua.png";
import imgAdvertise    from "/assets/images/contact-us/rethinkways-contact-us-04-Bbky39He.png";
import imgWorldMap     from "/assets/images/contact-us/rethinkways-contact-us-05-BlveAzR3.png";
import yesevaFont      from "/assets/fonts/YesevaOne-Regular-DOzKZiG_.ttf";

const videoWebm = assetUrl("/assets/videos/rethinkways-contact-banner.webm");
const videoMp4  = assetUrl("/assets/videos/rethinkways-contact-banner.mp4");

// ─── Dark Mode Sync ────────────────────────────────────────────────────────────
// Identical hook used across PrivacyPolicy & AboutUs — reads what the Header writes:
//   localStorage["trw-dark-mode"] === "1" | "0"
//   document.documentElement classList "trw-dark-mode"
//   document.body classList "trw-dark-mode" | "dark"

function getHeaderDarkMode() {
  if (typeof window === "undefined") return false;
  const stored = window.localStorage.getItem("trw-dark-mode");
  if (stored === "1") return true;
  if (stored === "0") return false;
  return (
    document.documentElement.classList.contains("trw-dark-mode") ||
    document.body.classList.contains("trw-dark-mode") ||
    document.body.classList.contains("dark")
  );
}

function useSyncedDarkMode() {
  const [isDark, setIsDark] = useState(getHeaderDarkMode);

  useEffect(() => {
    const observer = new MutationObserver(() => setIsDark(getHeaderDarkMode()));
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class", "style"],
    });
    const onStorage = (e) => {
      if (e.key === "trw-dark-mode") setIsDark(e.newValue === "1");
    };
    window.addEventListener("storage", onStorage);
    return () => {
      observer.disconnect();
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  return isDark;
}

// ─── Design Tokens (same pattern as AboutUs / PrivacyPolicy) ──────────────────

const tok = {
  pageBg:    (d) => ({ background: d ? "#000000" : "#ffffff" }),
  primary:   (d) => ({ color: d ? "#ffffff"                : "rgb(13,19,23)" }),
  muted:     (d) => ({ color: d ? "rgba(255,255,255,0.82)" : "#202226" }),
  heading:   (d) => ({ color: d ? "#ffffff"                : "#000000" }),
  icon:      (d) => ({ color: d ? "#ffffff"                : "#000000" }),
  red:             { color: "#FE0301" },
  font:            { fontFamily: "'Poppins', Helvetica, Arial, sans-serif" },
  yeseva:          { fontFamily: '"TRW Yeseva One", "Yeseva One", Georgia, serif' },
  transition:      "background 200ms, color 200ms",
};

// ─── Font injection ────────────────────────────────────────────────────────────

const FONT_CSS = `
  @font-face {
    font-family: "TRW Yeseva One";
    src: url("${yesevaFont}") format("truetype");
    font-weight: 400;
    font-style: normal;
    font-display: swap;
  }
`;

let fontInjected = false;
function injectFont() {
  if (fontInjected || typeof document === "undefined") return;
  const style = document.createElement("style");
  style.id = "trw-yeseva-font";
  style.textContent = FONT_CSS;
  document.head.appendChild(style);
  fontInjected = true;
}

// ─── Social links data ─────────────────────────────────────────────────────────

const SOCIAL_LINKS = [
  { label: "Instagram", href: "https://www.instagram.com/travel.rethinkways/",             icon: faInstagram  },
  { label: "WhatsApp",  href: "https://whatsapp.com/channel/0029Vb69sC2JJhzYpd1zRQ1C/",   icon: faWhatsapp   },
  { label: "Facebook",  href: "https://www.facebook.com/share/1CNm92gSxo/",                icon: faFacebookF  },
  { label: "LinkedIn",  href: "http://linkedin.com/company/travel-rethink-ways/",           icon: faLinkedinIn },
  { label: "Pinterest", href: "https://in.pinterest.com/travelrethinkways/",               icon: faPinterestP },
  { label: "YouTube",   href: "https://www.youtube.com/@Travel.Rethinkways",               icon: faYoutube    },
  { label: "Threads",   href: "https://www.threads.com/@travel.rethinkways",               icon: faThreads    },
  { label: "X",         href: "https://x.com/rethinkways/",                                icon: faXTwitter   },
];

// ─── Shared layout helpers ─────────────────────────────────────────────────────

const container = {
  margin: "0 auto",
  width: "min(1500px, 100%)",
  padding: "0 15px",
};

// Two-column responsive grid
function TwoCol({ children, gap = 50, minWidth = 420, alignItems = "start" }) {
  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${minWidth}px), 1fr))`,
      gap,
      alignItems,
    }}>
      {children}
    </div>
  );
}

// Section wrapper with consistent top margin
function Section({ id, children, mt = 96 }) {
  return (
    <section id={id} style={{ marginTop: mt }}>
      {children}
    </section>
  );
}

// ─── Contact Us Page ───────────────────────────────────────────────────────────

export default function ContactUs() {
  const isDark = useSyncedDarkMode();

  useEffect(() => { injectFont(); }, []);

  // Typography helpers (driven by isDark)
  const headingStyle = {
    margin: 0,
    fontSize: "clamp(36px, 4vw, 50px)",
    fontWeight: 600,
    lineHeight: 1.12,
    ...tok.heading(isDark),
    transition: tok.transition,
  };

  const bodyStyle = {
    fontSize: "clamp(18px, 1.8vw, 22px)",
    lineHeight: 1.65,
    ...tok.muted(isDark),
    transition: tok.transition,
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        width: "100%",
        overflowX: "clip",
        ...tok.pageBg(isDark),
        ...tok.font,
        WebkitFontSmoothing: "antialiased",
        transition: tok.transition,
      }}
    >

      {/* ── Hero Video Banner ── */}
      <section
        style={{
          position: "relative",
          height: "clamp(420px, 40vw, 682px)",
          width: "100%",
          overflow: "hidden",
          background: "#000",
        }}
      >
        <video
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
          autoPlay loop muted playsInline preload="auto" aria-hidden="true"
        >
          <source src={videoWebm} type="video/webm" />
          <source src={videoMp4}  type="video/mp4" />
        </video>

        {/* subtle overlay */}
        <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.20)" }} />

        <div style={{
          position: "relative", zIndex: 10,
          margin: "0 auto", maxWidth: 1500, height: "100%",
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: "0 20px", textAlign: "center",
        }}>
          <h1 style={{
            margin: 0,
            fontFamily: "Avenir Next, Avenir, Segoe UI, sans-serif",
            fontSize: "clamp(42px, 7vw, 98px)",
            lineHeight: 0.98,
            letterSpacing: "-0.03em",
            color: "#fff",
          }}>
            <span style={{ fontWeight: 300 }}>Redefining </span>
            <span style={{ fontWeight: 600 }}>Exploration.</span>
          </h1>
        </div>
      </section>

      {/* ── Main Content ── */}
      <main style={{ display: "flex", justifyContent: "center", ...tok.pageBg(isDark), transition: tok.transition }}>
        <div style={{ ...container, maxWidth: 1500 }}>

          {/* ── You're In The Right Place ── */}
	          <Section id="Youre_in_The_Right_Place" mt={4}>
	            <div style={{ textAlign: "center", paddingBottom: 24 }}>
	              <h2 style={{
	                marginTop: 32,
	                ...tok.yeseva,
	                fontSize: "clamp(36px, 4vw, 50px)",
	                fontWeight: 400,
	                lineHeight: 1.24,
	                letterSpacing: "0.01em",
	                ...tok.primary(isDark),
	                transition: tok.transition,
	              }}>
	                YOU'RE IN THE RIGHT PLACE
	              </h2>

              <h4 style={{
                margin: "16px auto 0",
                maxWidth: 980,
                ...tok.font,
                fontSize: "clamp(22px, 2.3vw, 35px)",
                lineHeight: 1.8,
                fontWeight: 400,
                ...tok.primary(isDark),
                transition: tok.transition,
              }}>
                <strong style={{ display: "block", fontWeight: 700 }}>
                  Need help planning your next escape?
                </strong>
                <strong style={{ display: "block", fontWeight: 700, marginTop: 0 }}>
                  Looking to collaborate with a growing travel brand?
                </strong>
              </h4>

              {/* Two-column: intro text + world map */}
              <div style={{ marginTop: 48 }}>
                <TwoCol gap={32} minWidth={420} alignItems="center">
                  {/* Left — text */}
                  <div style={{ maxWidth: 640, textAlign: "left" }}>
                    <p style={{ margin: 0, ...bodyStyle }}>
                      At Travel Rethink Ways, we believe curiosity drives insight and conversation drives
                      successful partnerships. We're a travel knowledge and storytelling platform, not a
                      booking portal or travel agency.
                    </p>
                    <p style={{ margin: "32px 0 0", ...bodyStyle }}>
                      Whether you're here to explore ideas, advertise your brand or partner with us,
                      our team is here to support you.
                    </p>
                    <p style={{ margin: "24px 0 0", textAlign: "left" }}>
                      <a
                        href="mailto:travel@rethinkways.com"
                        style={{
                          fontWeight: 700,
                          fontSize: "clamp(18px, 1.8vw, 22px)",
                          textDecoration: "none",
                          ...tok.red,
                        }}
                      >
                        travel@rethinkways.com
                      </a>
                    </p>
                  </div>

                  {/* Right — world map image */}
                  <div style={{ margin: "0 auto", width: "100%", maxWidth: 720, justifySelf: "end" }}>
                    <img
                      src={imgWorldMap}
                      alt="World Map"
                      style={{
                        width: "100%",
                        objectFit: "contain",
                        opacity: isDark ? 1 : 0.35,
                        filter: isDark ? "brightness(0) invert(1) contrast(1.3)" : "none",
                        transition: "opacity 200ms, filter 200ms",
                      }}
                    />
                  </div>
                </TwoCol>
              </div>
            </div>
          </Section>

          {/* ── Advertise With Us ── */}
          <Section id="Advertise_With_Us" mt={80}>
            <TwoCol gap={50} minWidth={420} alignItems="start">
              {/* Image — left */}
              <div style={{ margin: "0 auto", width: "100%", maxWidth: 700 }}>
                <img src={imgAdvertise} alt="Advertise With Us"
                  style={{ width: "100%", objectFit: "contain", opacity: isDark ? 0.9 : 1, transition: "opacity 200ms" }} />
              </div>

              {/* Text — right */}
              <div style={{ maxWidth: 700 }}>
                <h3 style={{ ...headingStyle, textAlign: "left" }}>Advertise With Us</h3>
                <p style={{ margin: "32px 0 0", ...bodyStyle, lineHeight: 2 }}>
                  We work with Hospitality &amp; Travel-focused brands including Tourism Boards &amp;
                  Destinations | Hotels, Resorts &amp; Unique Stays | Theme Parks &amp; Attractions |
                  Adventure &amp; Experience Providers | Cruise Lines &amp; Rail Journeys | Travel Gear
                  &amp; Luggage Brands | Airlines, Mobility &amp; Travel-Tech Brands | Travel Insurance
                  &amp; Ancillary Services. Brand in front of high-intent travellers at the moment
                  they're researching, comparing and deciding.
                </p>
                <p style={{ margin: "24px 0 0", ...bodyStyle, lineHeight: 1.8 }}>
                  We help brands get discovered before the booking begins
                </p>
                <ul style={{
                  margin: "32px 0 0",
                  paddingLeft: 32,
                  listStyle: "disc",
                  display: "grid",
                  gap: 16,
                  textAlign: "left",
                }}>
                  {["Knowledge-driven Travel Content", "Brand Features & Visibility", "Search-led Discovery"].map((item) => (
                    <li key={item} style={{
                      fontSize: "clamp(18px, 1.8vw, 22px)",
                      fontWeight: 600,
                      lineHeight: 1.4,
                      ...tok.primary(isDark),
                      transition: tok.transition,
                    }}>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </TwoCol>
          </Section>

          {/* ── Partner With Us ── */}
          <Section id="Partner_With_Us" mt={96}>
            <TwoCol gap={48} minWidth={420} alignItems="center">
              {/* Text — left */}
              <div style={{ textAlign: "left" }}>
                <h3 style={{ ...headingStyle, textAlign: "left" }}>Partner With Us</h3>
                <p style={{ margin: "32px 0 0", ...bodyStyle }}>
                  We collaborate with Tourism Boards, Travel Brands, Creators and Industry leaders
                  to co-create content that informs, inspires and builds long term credibility.
                </p>
                <p style={{ margin: "32px 0 0", ...bodyStyle }}>
                  If you believe travel should be thoughtful, responsible and insight led — we're aligned.
                </p>
              </div>

              {/* Image — right */}
              <div style={{ margin: "0 auto", width: "100%", maxWidth: 680, justifySelf: "end" }}>
                <img src={imgPartnerWith} alt="Partner With Us"
                  style={{ width: "100%", objectFit: "contain", opacity: isDark ? 0.9 : 1, transition: "opacity 200ms" }} />
              </div>
            </TwoCol>
          </Section>

          {/* ── Work With Us ── */}
          <Section id="Work_With_Us" mt={96}>
            <TwoCol gap={48} minWidth={420} alignItems="center">
              {/* Image — left (order swaps on mobile via grid auto-fit) */}
              <div style={{ margin: "0 auto", width: "100%", maxWidth: 680 }}>
                <img src={imgWorkWithUs} alt="Work With Us"
                  style={{ width: "100%", objectFit: "contain", opacity: isDark ? 0.9 : 1, transition: "opacity 200ms" }} />
              </div>

              {/* Text — right */}
              <div style={{ textAlign: "left" }}>
                <h3 style={{ ...headingStyle, textAlign: "left" }}>Work With Us</h3>
                <p style={{ margin: "32px 0 0", ...bodyStyle }}>
                  From content collaborations to industry opportunities, we work with professionals
                  shaping the future of travel storytelling.
                </p>
                <p style={{ margin: "24px 0 0", fontSize: "clamp(18px, 1.8vw, 22px)", ...tok.primary(isDark) }}>
                  <a
                    href="https://travel.rethinkways.com/jobs/"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontWeight: 700, textDecoration: "none", ...tok.red }}
                  >
                    View Job Openings
                  </a>
                </p>
              </div>
            </TwoCol>
          </Section>

          {/* ── Get In Touch ── */}
          <Section id="Get_In_Touch" mt={96}>
            <TwoCol gap={48} minWidth={420} alignItems="center">
              {/* Text — left */}
              <div style={{ textAlign: "left" }}>
                <h3 style={{ ...headingStyle, textAlign: "left" }}>Get In Touch</h3>
                <p style={{ margin: "32px 0 0", ...bodyStyle }}>
                  Have a question, idea, or opportunity to share?
                </p>

                <ul style={{ margin: "32px 0 0", padding: 0, listStyle: "none", display: "grid", gap: 16 }}>
                  {/* Email */}
                  <li style={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                    <FontAwesomeIcon icon={faEnvelope}
                      style={{ width: 24, marginRight: 12, flexShrink: 0, fontSize: 22, ...tok.icon(isDark) }}
                      aria-hidden="true" />
                    <a href="mailto:travel@rethinkways.com"
                      style={{ fontSize: "clamp(18px,1.8vw,22px)", lineHeight: 1.55, wordBreak: "break-all", textDecoration: "none", ...tok.primary(isDark), transition: tok.transition }}>
                      travel@rethinkways.com
                    </a>
                  </li>

                  {/* Website */}
                  <li style={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                    <FontAwesomeIcon icon={faGlobe}
                      style={{ width: 24, marginRight: 12, flexShrink: 0, fontSize: 22, ...tok.icon(isDark) }}
                      aria-hidden="true" />
                    <a href="https://travel.rethinkways.com" target="_blank" rel="noopener noreferrer"
                      style={{ fontSize: "clamp(18px,1.8vw,22px)", lineHeight: 1.55, wordBreak: "break-all", textDecoration: "none", ...tok.primary(isDark), transition: tok.transition }}>
                      www.travelrethinkways.com
                    </a>
                  </li>

                  {/* Phone */}
                  <li style={{ display: "flex", alignItems: "center" }}>
                    <FontAwesomeIcon icon={faPhone}
                      style={{ width: 24, marginRight: 12, flexShrink: 0, fontSize: 22, ...tok.icon(isDark) }}
                      aria-hidden="true" />
                    <a href="tel:+918438506813"
                      style={{ fontSize: "clamp(18px,1.8vw,22px)", lineHeight: 1.55, textDecoration: "none", ...tok.primary(isDark), transition: tok.transition }}>
                      +91 84385 06813
                    </a>
                  </li>

                  {/* Location */}
                  <li style={{ display: "flex", alignItems: "center" }}>
                    <FontAwesomeIcon icon={faLocationDot}
                      style={{ width: 24, marginRight: 12, flexShrink: 0, fontSize: 22, ...tok.icon(isDark) }}
                      aria-hidden="true" />
                    <span style={{ fontSize: "clamp(18px,1.8vw,22px)", lineHeight: 1.55, ...tok.primary(isDark), transition: tok.transition }}>
                      Chennai, Tamil Nadu, India
                    </span>
                  </li>
                </ul>
              </div>

              {/* Image — right */}
              <div style={{ margin: "0 auto", width: "100%", maxWidth: 680, justifySelf: "end" }}>
                <img src={imgGetInTouch} alt="Get In Touch"
                  style={{ width: "100%", objectFit: "contain", opacity: isDark ? 0.9 : 1, transition: "opacity 200ms" }} />
              </div>
            </TwoCol>
          </Section>

          {/* ── Connect With Us ── */}
          <Section id="Connect_With_Us" mt={96}>
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 400px), 1fr))",
              gap: 40,
              alignItems: "center",
            }}>
              {/* Left — text */}
              <div style={{ maxWidth: 760, textAlign: "left" }}>
                <h3 style={{ ...headingStyle, textAlign: "left" }}>Connect With Us</h3>
                <p style={{ margin: "32px 0 0", ...bodyStyle, lineHeight: 1.55 }}>
                  Follow Travel Rethink Ways for travel knowledge,<br />
                  destination insights, and industry perspectives beyond<br />
                  bookings.
                </p>
                <p style={{ margin: "32px 0 0", ...bodyStyle, lineHeight: 1.55 }}>
                  Travel Rethink Ways is not a booking portal or travel<br />
                  agency. We are a travel visibility &amp; knowledge<br />
                  platform.
                </p>
              </div>

              {/* Right — social icon grid */}
              <div style={{ width: "100%", maxWidth: 312, justifySelf: "center" }}>
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  justifyItems: "center",
                  gap: 18,
                }}>
                  {SOCIAL_LINKS.map(({ label, href, icon }) => (
                    <a
                      key={label}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={label}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: 64,
                        width: 64,
                        borderRadius: 10,
                        background: isDark ? "#222" : "#000",
                        color: "#fff",
                        transition: "opacity 200ms, background 200ms",
                        textDecoration: "none",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.85")}
                      onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
                    >
                      <FontAwesomeIcon icon={icon} style={{ fontSize: 36 }} aria-hidden="true" />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </Section>

          {/* ── Bottom padding ── */}
          <div style={{ height: 50 }} />
        </div>
      </main>
    </div>
  );
}
